/* eslint-disable @typescript-eslint/no-unused-expressions, check-file/filename-blocklist, react-hooks/immutability, react-refresh/only-export-components -- pre-existing in this file; the change here only swaps the selection checkbox for the design-system one */
import { KText } from '@/ui-library/components';
import {
  Box,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
} from '@chakra-ui/react';
import { IndeterminateCheckbox } from './useSelectAllColumn';

// The shared design-system checkbox (see useSelectAllColumn).
// Helper functions
const createLimitHandler = (
  rows,
  selectableRows,
  selectedCount,
  disableSpecificRowFn
) => {
  const handleSelectAll = () => {
    // No limit, select all rows that aren't disabled by disableSpecificRowFn
    rows.forEach((row) => {
      if (!disableSpecificRowFn(row.original)) {
        row.toggleRowSelected(true);
      }
    });
  };

  const handleLimitedSelect = () => {
    const availableToSelect = selectableRows - selectedCount;
    if (availableToSelect <= 0) return;

    let countSelected = 0;
    rows.forEach((row) => {
      // Skip rows that are disabled by disableSpecificRowFn
      if (disableSpecificRowFn(row.original)) return;

      if (!row.isSelected && countSelected < availableToSelect) {
        row.toggleRowSelected(true);
        countSelected++;
      }
    });
  };

  const handleDeselectAll = () => {
    // Deselect all rows regardless of limit
    rows.forEach((row) => {
      if (row.isSelected) {
        row.toggleRowSelected(false);
      }
    });
  };

  return { handleSelectAll, handleLimitedSelect, handleDeselectAll };
};

// Header component that handles "select all" functionality
const SelectionHeader = ({
  getToggleAllRowsSelectedProps,
  state,
  rows,
  selectableRows,
  disableSpecificRowFn,
}) => {
  const allProps = getToggleAllRowsSelectedProps();
  const hasLimit = selectableRows !== null && selectableRows !== undefined;
  const selectedCount = Object.keys(state.selectedRowIds).length;

  // Count of rows that can be selected (not disabled by disableSpecificRowFn)
  const selectableRowCount = rows.filter(
    (row) => !disableSpecificRowFn(row.original)
  ).length;

  const selectableAndSelectedCount = rows.filter(
    (row) => !disableSpecificRowFn(row.original) && row.isSelected
  ).length;

  const isHeaderDisabled =
    selectableRowCount === 0 || (hasLimit && selectableRows <= 0);

  const isHeaderChecked =
    !isHeaderDisabled &&
    selectableRowCount > 0 &&
    selectableAndSelectedCount ===
      Math.min(
        selectableRowCount,
        hasLimit ? selectableRows : selectableRowCount
      );

  const { handleSelectAll, handleLimitedSelect, handleDeselectAll } =
    createLimitHandler(
      rows,
      selectableRows,
      selectedCount,
      disableSpecificRowFn
    );

  const customOnChange = (e) => {
    const isChecked = e.target.checked;

    if (isChecked) {
      hasLimit ? handleLimitedSelect() : handleSelectAll();
    } else {
      handleDeselectAll();
    }
  };

  return (
    <div>
      <IndeterminateCheckbox
        {...allProps}
        onChange={customOnChange}
        checked={isHeaderChecked}
        disabled={isHeaderDisabled}
        style={{
          cursor: isHeaderDisabled ? 'not-allowed' : 'pointer',
          opacity: isHeaderDisabled ? 0.5 : 1,
        }}
      />
    </div>
  );
};

// Cell component that handles individual row selection
const SelectionCell = ({
  row,
  state,
  selectableRows,
  disableSpecificRowFn,
}) => {
  const rowProps = row.getToggleRowSelectedProps();

  const isSpecificRowDisabled = disableSpecificRowFn(row.original);
  const hasLimit = selectableRows !== null && selectableRows !== undefined;
  const selectedCount = Object.keys(state.selectedRowIds).length;
  const isLimitReached =
    !row.isSelected && hasLimit && selectedCount >= selectableRows;

  const disabled = isSpecificRowDisabled || isLimitReached;
  row.isRowDisabled = disabled;

  if (disabled && !isSpecificRowDisabled) {
    return (
      <Box position="relative">
        <Popover placement="bottom" trigger="hover">
          <PopoverTrigger>
            <Box display="inline-block">
              <Box
                position="absolute"
                cursor="not-allowed"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex={99999}
              />
              <IndeterminateCheckbox
                {...row.getToggleRowSelectedProps()}
                disabled={disabled}
              />
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent width="300px" variants="default" fontSize="sm">
              <PopoverArrow />
              <PopoverBody>
                <KText>
                  {selectedCount <= 0
                    ? `You are all out of available seats for your account. If you want more, please`
                    : `The selected ${selectedCount}  ${
                        selectedCount === 1 ? 'user' : 'users'
                      } will consume all the remaining seats in your account. If you want more, please `}
                  <KText
                    display="inline-block"
                    color="primary-500"
                    size="sm-medium"
                    cursor="pointer"
                    onClick={() => {
                      window.Pylon('show');
                    }}
                  >
                    {' contact support.'}
                  </KText>
                </KText>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
    );
  }

  return (
    <div>
      <IndeterminateCheckbox
        {...rowProps}
        disabled={disabled}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
        }}
      />
    </div>
  );
};

export function useCustomRowSelectColumn(hooks) {
  hooks.visibleColumns.push((columns, { instance }) => {
    const selectableRows = instance?.selectableRows;
    const disableSpecificRowFn =
      instance?.disableSpecificRowFn || (() => false);

    return [
      {
        id: 'selection',
        disableResizing: true,
        disableGroupBy: true,
        disableSortBy: true,
        disableFilters: true,
        width: 40,
        Header: ({ getToggleAllRowsSelectedProps, state, rows }) => (
          <SelectionHeader
            getToggleAllRowsSelectedProps={getToggleAllRowsSelectedProps}
            state={state}
            rows={rows}
            selectableRows={selectableRows}
            disableSpecificRowFn={disableSpecificRowFn}
          />
        ),
        Cell: ({ row, state }) => (
          <SelectionCell
            row={row}
            state={state}
            selectableRows={selectableRows}
            disableSpecificRowFn={disableSpecificRowFn}
          />
        ),
      },
      ...columns,
    ];
  });
}
